import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { AnimatePresence } from 'framer-motion'
import { RequestContext, RequestProvider } from '../../context/RequestContext'
import { openModal } from '../../service/modal-service'
import { t } from '../../utils/markets'
import { getMarketRequestSettings } from '../../lib/market-settings'
import { localiseRequestSettings } from '../../utils/market-labels'
import { SystemError } from '../error/SystemError'
import { BottomButtons } from '../index/service-category/aircon-service/BottomButtons'

// Sheets are only needed after a click; keep them out of the page bundle.
const Services = dynamic(() => import('../index/service/Services').then((m) => m.Services), { ssr: false })
const AirconItemSelection = dynamic(() => import('../index/service-category/aircon-service/AirconItemSelection').then((m) => m.AirconItemSelection), { ssr: false })
const AirconScope = dynamic(() => import('../index/service-category/aircon-service/AirconScope').then((m) => m.AirconScope), { ssr: false })

const MarketBookingContext = createContext(null)

export const useMarketBooking = () => useContext(MarketBookingContext)

const PT = 'Fitness Trainer'

/**
 * Booking for a UAE / Malaysia page, wired exactly like the Singapore pages
 * (Aircon.js / AirconRequestSection, PersonalTrainer.js / PersonalTrainerHead)
 * so the same SG components drive it. Wrap the page body; descendants read the
 * state below through useMarketBooking().
 *
 * Aircon: the Services form opens as a sheet, "More options" opens
 * AirconItemSelection, "Service Details" opens AirconScope, and the sticky
 * Book / WhatsApp bar (BottomButtons) shows while no form is open.
 * Personal trainer pages with the hero pass `inlineForm`: the Services form
 * renders inside the hero (PersonalTrainerHead reads requestForm), as on the
 * SG page. Pages without that hero (guides) get the sheet.
 *
 * Booking settings: the build-time REQUEST_CATEGORY_SETTINGS rows for the
 * country. If the build had none (backend not migrated yet, or the fetch
 * failed), opening the form re-fetches them in the browser first; if the
 * backend still has none, a "temporarily unavailable" notice shows instead of
 * a form that cannot submit.
 *
 * book(tile) is the one-call entry for price tiles and buttons:
 *  - tile.criteria: aircon quick book (straight to date and time);
 *  - tile.quickBook: the PT free consultation;
 *  - tile.package: a PT package, preselected for PtPlanAndPostalCode;
 *  - none: the form opens at its question step.
 */
export const MarketBooking = ({ market, city, requestType, requestCategorySettings, inlineForm = false, children }) => (
    <RequestProvider>
        <MarketBookingInner
            market={market}
            city={city}
            requestType={requestType}
            requestCategorySettings={requestCategorySettings}
            inlineForm={inlineForm}
        >
            {children}
        </MarketBookingInner>
    </RequestProvider>
)

const MarketBookingInner = ({ market, city, requestType, requestCategorySettings, inlineForm, children }) => {
    const {
        setRequestCriteria, setRequestDescription, setServiceSummary, setSelectedPackage,
        setHeadline, setServiceCost, setOriginalCost,
    } = useContext(RequestContext)

    const isPt = requestType === PT
    const [settings, setSettings] = useState(Array.isArray(requestCategorySettings) ? requestCategorySettings : [])
    const settingsRef = useRef(settings)

    const [requestForm, setRequestForm] = useState(false)
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)
    const [isShowAirconItemSelection, setIsShowAirconItemSelection] = useState(false)
    const [isShowJobScope, setIsShowJobScope] = useState(false)
    const [scope, setScope] = useState({ serviceType: 'general', unitType: '', unit: '', cost: '' })
    const [isSystemError, setIsSystemError] = useState(false)

    // Resolves true once booking settings exist for this country.
    const ensureSettings = useCallback(async () => {
        if (settingsRef.current.length > 0) return true
        const fresh = await getMarketRequestSettings(market.countryName, requestType)
        if (fresh.length > 0) {
            settingsRef.current = fresh
            setSettings(fresh)
            return true
        }
        setIsSystemError(true)
        return false
    }, [market.countryName, requestType])

    // SG PersonalTrainer.js openRequestForm: back to the hero, form inline.
    const openRequestForm = useCallback(async () => {
        if (!(await ensureSettings())) return
        if (inlineForm) globalThis.scrollTo({ top: 0 })
        openModal(setRequestForm)
    }, [ensureSettings, inlineForm])

    const openItemSelection = useCallback(async () => {
        if (!(await ensureSettings())) return
        setIsQuickBook(false)
        setIsShowAirconItemSelection(true)
    }, [ensureSettings])

    // Same signature as the SG pages' showJobScope.
    const showJobScope = useCallback((serviceType, unitType, unit, cost) => {
        setScope({ serviceType, unitType, unit, cost })
        openModal(setIsShowJobScope)
    }, [])

    const book = useCallback(async (tile = {}) => {
        // Aircon never uses the question wizard: a booking without a chosen
        // service (guide "Book" buttons and the like) opens the item picker.
        if (!isPt && !tile.criteria) {
            await openItemSelection()
            return
        }
        if (tile.criteria) {
            setRequestCriteria(tile.criteria)
            setRequestDescription(isPt ? 'Personal Trainer' : t(market, 'acCategory'))
            setServiceSummary(tile.summary || '')
            setIsQuickBook(true)
        } else if (tile.quickBook) {
            // PersonalTrainerHead.quickBookPT: no package, no price.
            setRequestCriteria([])
            setRequestDescription(tile.description || '')
            setServiceCost(0)
            setOriginalCost(0)
            setIsQuickBook(true)
        } else if (tile.package) {
            // PersonalTrainerBooking: the package name is the description.
            setRequestDescription(tile.package.packageName)
            setIsQuickBook(true)
        } else {
            setRequestCriteria([])
            setIsQuickBook(false)
        }
        if (isPt) setHeadline(tile.headline || 'Get my trainer & diet plan')
        setIsCustomBook(false)
        // PtPlanAndPostalCode prices a preselected package with the market's modifiers.
        setSelectedPackage(tile.package ?? null)
        await openRequestForm()
    }, [isPt, market, openRequestForm, openItemSelection, setRequestCriteria, setRequestDescription, setServiceSummary,
        setSelectedPackage, setHeadline, setServiceCost, setOriginalCost])

    // Local question wording and choice labels; the values stay backend keys.
    const shownSettings = useMemo(() => localiseRequestSettings(settings, market), [settings, market])

    const value = {
        market, city, requestType, settings: shownSettings,
        requestForm, setRequestForm, isQuickBook, setIsQuickBook, isCustomBook, setIsCustomBook,
        openRequestForm, openItemSelection, showJobScope, book,
    }

    return (
        <MarketBookingContext.Provider value={value}>
            {children}

            {!inlineForm && requestForm && (
                <Services
                    requestType={requestType}
                    requestCategorySettings={shownSettings}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    asSheet
                />
            )}
            {!isPt && !requestForm && (
                <BottomButtons countryName={market.countryName} selfHelp={false} />
            )}
            {isShowAirconItemSelection && (
                <AnimatePresence>
                    <AirconItemSelection
                        setIsShowAirconItemSelection={setIsShowAirconItemSelection}
                        setRequestForm={setRequestForm}
                        setIsCustomBook={setIsCustomBook}
                        setIsQuickBook={setIsQuickBook}
                        countryName={market.countryName}
                        userId={null}
                    />
                </AnimatePresence>
            )}
            {isShowJobScope && (
                <AnimatePresence>
                    <AirconScope
                        setIsShowJobScope={setIsShowJobScope}
                        serviceType={scope.serviceType}
                        unit={scope.unit}
                        unitType={scope.unitType}
                        cost={scope.cost}
                        market={market}
                    />
                </AnimatePresence>
            )}
            <AnimatePresence>
                {isSystemError && (
                    <SystemError
                        errorContent={{
                            header: 'Booking is temporarily unavailable',
                            body: 'Please try again shortly. If it keeps happening, email support@stringssg.com.',
                        }}
                        setIsSystemError={setIsSystemError}
                    />
                )}
            </AnimatePresence>
        </MarketBookingContext.Provider>
    )
}
